import React, {
  createContext,
  useContext,
  useRef,
  useState,
  Dispatch,
} from "react";
import Dropzone from "dropzone";
import dropzoneTranslations from "../../../util/dropzoneTranslations";
import { ModuleContext, ProductContext, CanvasContext } from ".";
import { ModuleInfoImage } from "../../../Types";
import { UploadFromUrlResponse } from "../component/ImageTools/SocialNetwork/FBImages";
import { fetchApi } from "../../../config/core/Api";

let igOauthUrl = "https://www.instagram.com/oauth/authorize";
let clientId = "275739560352240";

interface ImageProviderProps {
  children: JSX.Element;
}

interface CanvasContextTypes {
  selectedUploadType: "device" | "facebook" | "instagram" | null;
  setSelectedUploadType: Dispatch<"device" | "facebook" | "instagram" | null>;
  addPhotoWithUrl: (
    imagesObj: UploadFromUrlResponse,
    filteredImgId: number,
    originalImgId: number
  ) => void;
  handleStateForCropperUploads: (url: string, selectedImageUrl: string) => void;
  initDropZone: () => void;
  instagramLogin: () => void;
}

const IMAGE_CONTEXT_INITIAL_VALUES = {
  selectedUploadType: null,
  setSelectedUploadType: (
    selectedUploadType: "device" | "facebook" | "instagram" | null
  ) => undefined,
  addPhotoWithUrl: (
    imagesObj: UploadFromUrlResponse,
    filteredImgId: number,
    originalImgId: number
  ) => undefined,
  handleStateForCropperUploads: (url: string, selectedImageUrl: string) =>
    undefined,
  initDropZone: () => undefined,
  instagramLogin: () => undefined,
};

Dropzone.autoDiscover = false;
/**
 * Array of ModuleInfoImage, used to keep track of images selected by user.
 * The whole set of images (if there are 9) is not saved anywhere but is generated
 * on dropzone upload or remove file.
 */
let tempModuleImages: ModuleInfoImage[] = [];

/**
 * For clearance is being kept track aside also for dropzone files.
 * Beside this dropzone files are being kept track because,
 * user might not upload all photos at once,
 */
let dropZoneFiles: Dropzone.DropzoneFile[] = [];

let updateState;

export const ImageContext = createContext<CanvasContextTypes>({
  ...IMAGE_CONTEXT_INITIAL_VALUES,
});

function ImageProvider({ children }: ImageProviderProps) {
  let t = window.INITIAL_DATA.TRANSLATIONS;

  const { setActiveObject } = useContext(CanvasContext);
  const { productType } = useContext(ProductContext);
  const { moduleImages, setModuleImages } = useContext(ModuleContext);

  const [selectedUploadType, setSelectedUploadType] = useState<
    "device" | "facebook" | "instagram" | null
  >(null);

  const imgUploadDropzoneRef = useRef<Dropzone | null>(null);

  /**
   * @method updateState
   * @returns {void}
   * @description Standalone method to manage photos being rendered in the canvas.
   * Main dependency is tempModuleImages.
   */
  updateState = (): void => {
    if (productType === "text-logo") {
      let url: string = tempModuleImages[0].renderUrl;
      setModuleImages([
        {
          originalUrl: url,
          filteredUrl: url,
          croppedUrl: url,
          renderUrl: url,
          wcIdFiltered: tempModuleImages[0].wcIdFiltered,
          wcIdOriginal: tempModuleImages[0].wcIdOriginal,
        },
      ]);
    } else if (productType === "only-image" || productType === "pop-art") {
      let image: ModuleInfoImage[] = [];

      tempModuleImages.map((url) => {
        image.push({
          originalUrl: url.originalUrl,
          filteredUrl: url.filteredUrl,
          croppedUrl: url.croppedUrl,
          renderUrl: url.renderUrl,
          wcIdFiltered: url.wcIdFiltered,
          wcIdOriginal: url.wcIdOriginal,
        });
      });

      for (let i = image.length; i < moduleImages.length; i++) {
        for (let j = 0; j < image.length; j++) {
          const element = image[j];
          if (image.length == moduleImages.length) {
            break;
          } else {
            image.push(element);
          }
        }
        break;
      }

      if (image.length) {
        setModuleImages([...image]);
      } else {
        window.canvasRef.getObjects("image").forEach((obj) => {
          window.canvasRef.remove(obj);
        });
      }

      // clear active object in react state
      setActiveObject(null);
      // console.log(image);
      // console.log(rawImage);
    }
    toggleRemoveLinks(true);
  };

  /**
   * @method initDropZone
   * @description Method use to initialize Dropzone
   */
  function initDropZone() {
    let maxFiles =
      productType === "only-image" ? 9 : productType === "pop-art" ? 3 : 1;

    new Dropzone(
      document.getElementById("image-upload-dropzone"),
      Object.assign(
        {
          url:
            productType === "pop-art"
              ? `${window.woocommerce_params.ajax_url}?action=upload_filtered_image`
              : `${window.woocommerce_params.ajax_url}?action=upload_image`,
          maxFiles: maxFiles,
          parallelUploads: maxFiles,
          uploadMultiple: true,
          timeout: 360000,
          acceptedFiles: ".jpg,.jpeg,.png",
          maxFilesize: 40,
          addRemoveLinks: true,
          resizeWidth: 1000,
          init: function() {
            imgUploadDropzoneRef.current = this;
          },
          removedfile(file: Dropzone.DropzoneFile) {
            file.previewElement.remove();
            handleFileRemove(file);
          },
          completemultiple(files: Dropzone.DropzoneFile[]): void {
            handleFileComplete(files);
          },
          addedfiles(files: Dropzone.DropzoneFile[]): void {
            toggleRemoveLinks(false);
          },
        },
        dropzoneTranslations()
      )
    );
  }

  /**
   * @method toggleRemoveLinks
   * @param visibility {boolean}
   * @returns void
   * @description This method visually hides the possibility from
   * the user to remove a photo on the upload proccess.
   */
  function toggleRemoveLinks(visibility: boolean): void {
    let removeLinks: HTMLCollectionOf<HTMLElement> = document
      .getElementById("image-upload-dropzone")
      .getElementsByClassName("dz-remove") as HTMLCollectionOf<HTMLElement>;

    for (let i = 0; i < removeLinks.length; i++) {
      const element: HTMLElement = removeLinks[i];
      element.style.display = visibility ? "block" : "none";
    }
  }

  /**
   * @method handleFileRemove
   * @param file {Dropzone.DropzoneFile}
   * @returns void
   * @description Callback of dropzone.
   * Main dependency of this method is:
   * the file name of the object in dropZoneFiles array.
   * If that name is found that file is removed on that index in:
   * 1-dropZoneFiles, 2-tempModuleImages.
   */
  function handleFileRemove(file: Dropzone.DropzoneFile): void {
    let dzFilesLength = dropZoneFiles.length;
    let cnt = Math.floor(9 / dzFilesLength);
    for (let i = 0; i < dzFilesLength; i++) {
      const dzFile = dropZoneFiles[i];
      if (dzFile.name === file.name) {
        deleteImageCallback([
          tempModuleImages[i].wcIdFiltered,
          tempModuleImages[i].wcIdOriginal,
        ]);
        dropZoneFiles.splice(i, 1);
        tempModuleImages.splice(i, 1);
        break;
      }
    }

    updateState();

    // bug fix on dropzone to keep message hidden if there are elements
    if (dropZoneFiles.length == 0) {
      // @ts-ignore
      document.querySelector(".dz-message").style.display = "flex";
    } else {
      // @ts-ignore
      document.querySelector(".dz-message").style.display = "none";
    }
  }

  /**
   * @method handleFileComplete
   * @param files {Dropzone.DropzoneFile[]}
   * @returns void
   * @description Callback of dropzone.
   * Method that handles state update for: 1-dropZoneFiles, 2-tempModuleImages.
   */
  function handleFileComplete(files: Dropzone.DropzoneFile[]): void {
    if (files[0].xhr) {
      let tempParsedResponse = JSON.parse(files[0].xhr.response);
      tempParsedResponse.map(
        (img: {
          filtered: string;
          original: string;
          original_id: number;
          filtered_id: number;
        }) => {
          if (productType === "pop-art") {
            tempModuleImages.push({
              originalUrl: img.original,
              filteredUrl: img.filtered,
              croppedUrl: img.original,
              renderUrl: img.filtered,
              wcIdFiltered: img.filtered_id,
              wcIdOriginal: img.original_id,
            });
          } else {
            tempModuleImages.push({
              originalUrl: img.original,
              filteredUrl: img.original,
              croppedUrl: img.original,
              renderUrl: img.original,
              wcIdFiltered: img.original_id,
              wcIdOriginal: img.original_id,
            });
          }
        }
      );
      files.map((file) => {
        dropZoneFiles.push(file);
      });
      updateState();
    }
  }

  /**
   * @method addPhotoWithUrl
   * @param imagesObj {UploadFromUrlResponse}
   * @returns void
   * @description Method that takes 2 possible keys: 1-filtered, 2-original.
   * It updates state for tempModuleImages and dropZoneFiles.
   */
  function addPhotoWithUrl(
    imagesObj: UploadFromUrlResponse,
    filteredImgId: number,
    originalImgId: number
  ): void {
    let mockFile = { name: `${Math.round(new Date().getTime())}`, size: 12345 };
    let callback = null; // Optional callback when it's done
    let crossOrigin = null; // Added to the `img` tag for crossOrigin handling
    let resizeThumbnail = false; // Tells Dropzone whether it should resize the image first

    imgUploadDropzoneRef.current.displayExistingFile(
      mockFile,
      imagesObj.original,
      callback,
      crossOrigin,
      resizeThumbnail
    );

    if (productType === "pop-art") {
      tempModuleImages.push({
        originalUrl: imagesObj.original,
        filteredUrl: imagesObj.filtered,
        croppedUrl: imagesObj.original,
        renderUrl: imagesObj.filtered,
        wcIdFiltered: filteredImgId,
        wcIdOriginal: originalImgId,
      });
    } else {
      tempModuleImages.push({
        originalUrl: imagesObj.original,
        filteredUrl: imagesObj.original,
        croppedUrl: imagesObj.original,
        renderUrl: imagesObj.original,
        wcIdFiltered: originalImgId,
        wcIdOriginal: originalImgId,
      });
    }

    // @ts-ignore
    dropZoneFiles.push(mockFile);
    updateState();
  }

  /**
   * @method handleStateForCropperUploads
   * @param url {string} Url of the cropped image
   * @param selectedImageUrl {string} Url of the original image set to cropper
   * @returns void
   * @description If selectedImageUrl matches to any of the existing object in tempModuleImages
   * croppedUrl and renderUrl of that object are changed.
   */
  function handleStateForCropperUploads(
    url: string,
    selectedImageUrl: string
  ): void {
    tempModuleImages.map((image, index) => {
      if (image.originalUrl == selectedImageUrl) {
        tempModuleImages[index] = {
          ...tempModuleImages[index],
          originalUrl: image.originalUrl,
          filteredUrl: image.filteredUrl,
          croppedUrl: url,
          renderUrl: url,
        };
      }
    });

    updateState();
  }

  /**
   * @method deleteImageCallback
   * @param {number[]} id
   * @returns {void} void
   * @description Method used to delete images that user removes from dropzone without
   * rendering to canvas.
   */
  function deleteImageCallback(id: number[]) {
    fetchApi({
      method: "POST",
      url: "?action=delete_image",
      data: { ids: id },
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((r) => {})
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {});
  }

  function instagramLogin() {
    window.open(
      `${igOauthUrl}?client_id=${clientId}&redirect_uri=${window.location.origin}/instagram-authenticator/&scope=user_profile,user_media&response_type=code`,
      `${t.instagram_authenticate} | papername`
    );
  }

  const providerValue = {
    addPhotoWithUrl,
    handleStateForCropperUploads,
    initDropZone,
    deleteImageCallback,
    instagramLogin,
    selectedUploadType,
    setSelectedUploadType,
  };

  return (
    <ImageContext.Provider value={providerValue}>
      {children}
    </ImageContext.Provider>
  );
}

export default ImageProvider;
